// Scoring grid configuration page (FRMW-004).
// T3 config screen — set target likelihood and impact level counts.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { scoringGridApi } from '@/lib/api/frameworkConfig'
import { scoringGridSchema, type ScoringGridFormData } from '@/lib/schemas/frameworkConfig'
import { queryKeys } from '@/lib/api/queryKeys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { useToast } from '@/lib/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { ApiRequestError } from '@/lib/api/client'

export function ScoringGridPage() {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const gridApi = scoringGridApi(api)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.frameworkConfig.scoringGrid(slug),
    queryFn: () => gridApi.get(),
    retry: (failureCount, err) => {
      // Don't retry on 404 (no config yet — that's expected)
      if (err instanceof ApiRequestError && err.status === 404) return false
      return failureCount < 3
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ScoringGridFormData>({
    resolver: zodResolver(scoringGridSchema),
    defaultValues: {
      target_likelihood_level_count: 5,
      target_impact_level_count: 5,
    },
  })

  // Populate form when data loads
  useEffect(() => {
    if (data) {
      reset({
        target_likelihood_level_count: data.target_likelihood_level_count,
        target_impact_level_count: data.target_impact_level_count,
      })
    }
  }, [data, reset])

  const mutation = useMutation({
    mutationFn: (formData: ScoringGridFormData) => gridApi.update(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.frameworkConfig.scoringGrid(slug) })
      toast({ title: t('scoringGrid.updated') })
    },
    onError: (err) => {
      toast({
        title: t('scoringGrid.updateFailed'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  function onSubmit(formData: ScoringGridFormData): void {
    mutation.mutate(formData)
  }

  if (isLoading) {
    return <FormSkeleton />
  }

  // Show error for non-404 errors (404 means no config yet, which is fine)
  if (error && !(error instanceof ApiRequestError && error.status === 404)) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('scoringGrid.title') })}
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('scoringGrid.title')}</h1>
        <p className="text-muted-foreground">{t('scoringGrid.description')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('scoringGrid.cardTitle')}</CardTitle>
          <CardDescription>{t('scoringGrid.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target_likelihood_level_count">
                {t('scoringGrid.likelihoodLabel')}
              </Label>
              <Input
                id="target_likelihood_level_count"
                type="number"
                min={2}
                max={10}
                {...register('target_likelihood_level_count', { valueAsNumber: true })}
              />
              {errors.target_likelihood_level_count && (
                <p className="text-sm text-destructive">
                  {errors.target_likelihood_level_count.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_impact_level_count">{t('scoringGrid.impactLabel')}</Label>
              <Input
                id="target_impact_level_count"
                type="number"
                min={2}
                max={10}
                {...register('target_impact_level_count', { valueAsNumber: true })}
              />
              {errors.target_impact_level_count && (
                <p className="text-sm text-destructive">
                  {errors.target_impact_level_count.message}
                </p>
              )}
            </div>

            <div className="flex items-center gap-4 pt-2">
              <Button type="submit" disabled={isSubmitting || !isDirty}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common:saving')}
                  </>
                ) : (
                  t('common:save')
                )}
              </Button>
              {data && (
                <p className="text-xs text-muted-foreground">
                  {t('common:lastUpdated', { date: new Date(data.updated_at).toLocaleString() })}
                </p>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
